import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  Fragment,
  lazy,
  memo,
} from "react";

import { Transition } from "@headlessui/react";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { CartDrawer } from "@cart/CartDrawer/CartDrawer.data";
import { CartDrawerAnalytics } from "@cart/CartDrawer/CartDrawerAnalytics.data";
import { CartDrawerBackdrop } from "@cart/CartDrawer/CartDrawerBackdrop.view";
import { useLiveRegion } from "@react/a11y/liveRegion";
import { useAddToCartManager } from "@react/hooks/useAddToCartManager";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  STOREFRONT_TRANSITION_EASING,
  STOREFRONT_TRANSITION_DURATION,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { useFocusManager } from "@tablescape/react/hooks/useFocusManager";
import {
  type CartSectionSettings,
  type TablescapeState,
} from "@tablescape/state";
import { tw } from "classnames";

const CartGiftNoteDrawer = lazy(
  () =>
    import(
      /* webpackExclude: /fixtures|stories|test/ */
      "@cart/CartGiftNoteDrawer/index"
    )
);
const CartProductVariantDrawer = lazy(
  () =>
    import(
      /* webpackExclude: /fixtures|stories|test/ */
      "@cart/CartProductVariantDrawer/index"
    )
);
const CartGiftWithPurchaseDrawer = lazy(
  () =>
    import(
      /* webpackExclude: /fixtures|stories|test/ */
      "@cart/CartGiftWithPurchaseDrawer/index"
    )
);

const styles = {
  container: tw(
    "flex",
    "justify-end",
    "border-none",
    "bg-transparent",
    "p-0",
    "pointer-events-auto",
    "w-screen"
  ),
  drawer: {
    enter: tw(
      "transition",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    enterFrom: tw("translate-x-full"),
    enterTo: tw("translate-x-0"),
    leave: tw(
      "transition",
      STOREFRONT_TRANSITION_EASING,
      STOREFRONT_TRANSITION_DURATION
    ),
    leaveFrom: tw("translate-x-0"),
    leaveTo: tw("translate-x-full"),
  },
  wrapper: tw("relative"),
};

const CartDrawerManagerBase = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    ui: {
      cartDrawerOpen,
      giftNoteDrawerOpen,
      giftWithPurchaseDrawerOpen,
      productVariantDrawerOpen,
    },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );

  const nestedDrawerOpen =
    giftNoteDrawerOpen ||
    giftWithPurchaseDrawerOpen ||
    productVariantDrawerOpen;
  const [nestedDrawerNode, setNestedDrawerNode] =
    useState<HTMLDivElement | null>(null);
  const nestedDrawerTriggerRef = useRef<HTMLElement | null>(null);
  const previousNestedDrawerOpenRef = useRef(false);
  const [preserveParentFocus, setPreserveParentFocus] = useState(false);
  const nestedDrawerActivationFrameRef = useRef<number | null>(null);

  const { activeProduct, toggleCartDrawer } = useCartActiveProduct();

  /**
   * A custom hook that manages the add to cart process.
   * This is the single most important hook in the Cart scope
   * It is responsible for:
   * - adding items to the cart
   * - adding custom cart attributes
   * - updating the cart item count
   * - updating the cart id
   * Be very careful when modifying this hook
   * @returns {void}
   */
  useAddToCartManager();

  useEffect(() => {
    if (cartDrawerOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [cartDrawerOpen]);

  const closeCartDrawer = useCallback(() => {
    toggleCartDrawer({
      cartDrawerOpen: false,
      cartDrawerUserToggled: false,
      giftNoteDrawerOpen: false,
      giftWithPurchaseDrawerOpen: false,
      productVariantDrawerOpen: false,
    });
  }, [toggleCartDrawer]);

  const setCartAnimationState = useCallback((cartDrawerAnimating: boolean) => {
    TablescapeContext.emit(TablescapeEvent.CART_ANIMATION_STATE, {
      ui: {
        cartDrawerAnimating,
      },
    });
  }, []);

  const { activate, deactivate, setContainer } = useFocusManager({
    active: cartDrawerOpen && !nestedDrawerOpen,
    focusTrapOptions: preserveParentFocus ? { initialFocus: false } : undefined,
    returnFocusOnDeactivate: !nestedDrawerOpen,
  });

  const {
    activate: activateNestedDrawer,
    deactivate: deactivateNestedDrawer,
    previouslyFocusedElement: nestedDrawerTrigger,
    setContainer: setNestedDrawerContainer,
  } = useFocusManager({
    active: nestedDrawerOpen,
    returnFocusOnDeactivate: true,
  });

  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      setContainer(node);
    },
    [setContainer]
  );

  const setNestedDrawerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (nestedDrawerActivationFrameRef.current !== null) {
        cancelAnimationFrame(nestedDrawerActivationFrameRef.current);
        nestedDrawerActivationFrameRef.current = null;
      }

      setNestedDrawerNode(node);
      setNestedDrawerContainer(node);

      if (node && nestedDrawerOpen) {
        nestedDrawerActivationFrameRef.current = requestAnimationFrame(() => {
          if (node.isConnected) {
            activateNestedDrawer();
          }

          nestedDrawerActivationFrameRef.current = null;
        });
      }
    },
    [activateNestedDrawer, nestedDrawerOpen, setNestedDrawerContainer]
  );

  useEffect(() => {
    if (nestedDrawerTrigger && nestedDrawerTrigger.isConnected) {
      nestedDrawerTriggerRef.current = nestedDrawerTrigger;
    }
  }, [nestedDrawerTrigger]);

  useEffect(() => {
    if (!cartDrawerOpen) {
      if (nestedDrawerActivationFrameRef.current !== null) {
        cancelAnimationFrame(nestedDrawerActivationFrameRef.current);
        nestedDrawerActivationFrameRef.current = null;
      }

      deactivateNestedDrawer();
      return;
    }

    if (nestedDrawerOpen) {
      if (!nestedDrawerNode) {
        return;
      }

      const frame = requestAnimationFrame(() => {
        if (nestedDrawerNode.isConnected) {
          activateNestedDrawer();
        }
      });

      return () => {
        cancelAnimationFrame(frame);
      };
    }

    if (nestedDrawerActivationFrameRef.current !== null) {
      cancelAnimationFrame(nestedDrawerActivationFrameRef.current);
      nestedDrawerActivationFrameRef.current = null;
    }

    deactivateNestedDrawer();
  }, [
    activateNestedDrawer,
    cartDrawerOpen,
    deactivateNestedDrawer,
    nestedDrawerNode,
    nestedDrawerOpen,
  ]);

  useEffect(() => {
    const previouslyOpen = previousNestedDrawerOpenRef.current;

    if (!previouslyOpen && nestedDrawerOpen) {
      setPreserveParentFocus(false);
    }

    if (previouslyOpen && !nestedDrawerOpen) {
      setPreserveParentFocus(true);

      const trigger = nestedDrawerTriggerRef.current;

      if (trigger?.isConnected) {
        requestAnimationFrame(() => {
          trigger.focus();
        });
      }
    }

    previousNestedDrawerOpenRef.current = nestedDrawerOpen;
  }, [nestedDrawerOpen]);

  useEffect(() => {
    if (preserveParentFocus && !nestedDrawerOpen) {
      const frame = requestAnimationFrame(() => {
        setPreserveParentFocus(false);
      });

      return () => {
        cancelAnimationFrame(frame);
      };
    }
  }, [preserveParentFocus, nestedDrawerOpen]);

  useEffect(() => {
    if (!cartDrawerOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" && event.key !== "Esc") {
        return;
      }

      const container = containerRef.current;

      if (container && !container.contains(event.target as Node)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (productVariantDrawerOpen) {
        toggleCartDrawer({ productVariantDrawerOpen: false });
        return;
      }

      if (giftWithPurchaseDrawerOpen) {
        toggleCartDrawer({ giftWithPurchaseDrawerOpen: false });
        return;
      }

      if (giftNoteDrawerOpen) {
        toggleCartDrawer({ giftNoteDrawerOpen: false });
        return;
      }

      closeCartDrawer();
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    cartDrawerOpen,
    closeCartDrawer,
    giftNoteDrawerOpen,
    giftWithPurchaseDrawerOpen,
    productVariantDrawerOpen,
    toggleCartDrawer,
  ]);

  const {
    text: {
      btnAddLabel,
      btnCloseLabel,
      bundleValueText,
      cartDiscountTitle,
      cartProductGiftItemButtonEditTextLabel,
      cartProductGiftItemButtonTextLabel,
      cartProductPage,
      cartSpendingGiftButtonTextLabel,
      cartSpendingGiftTitleLabel,
      dataErrorMessage,
      dataGiftNoteDrawer,
      dataGiftNoteStatus,
      linkAriaLabel,
      subtotalLabelCartDrawer,
      valueLabel,
    },
  } = useSectionSettings<CartSectionSettings>();

  const { announce } = useLiveRegion();

  const handleDrawerEnter = useCallback(() => {
    activate();
    announce("Cart drawer opened");
  }, [activate, announce]);

  const handleDrawerLeave = useCallback(() => {
    if (nestedDrawerOpen) {
      return;
    }

    deactivate();
    announce("Cart drawer closed");
  }, [deactivate, nestedDrawerOpen, announce]);

  return (
    <>
      <Transition
        afterEnter={() => {
          setCartAnimationState(false);
          handleDrawerEnter();
        }}
        afterLeave={() => {
          setCartAnimationState(false);
          handleDrawerLeave();
        }}
        appear={true}
        as={Fragment}
        beforeEnter={() => setCartAnimationState(true)}
        beforeLeave={() => setCartAnimationState(true)}
        show={cartDrawerOpen}
      >
        <div className={styles.container}>
          {/* Background overlay */}
          <CartDrawerBackdrop closeCartDrawer={closeCartDrawer} />

          <div
            aria-modal="true"
            className={styles.wrapper}
            ref={combinedRef}
            role="dialog"
            tabIndex={-1}
          >
            <CartDrawer
              btnAddLabel={btnAddLabel}
              btnCloseLabel={btnCloseLabel}
              bundleValueText={bundleValueText}
              cartDiscountTitle={cartDiscountTitle}
              cartLinkTo="/cart"
              cartProductGiftItemButtonEditTextLabel={
                cartProductGiftItemButtonEditTextLabel
              }
              cartProductGiftItemButtonTextLabel={
                cartProductGiftItemButtonTextLabel
              }
              cartProductPage={cartProductPage}
              cartSpendingGiftButtonTextLabel={cartSpendingGiftButtonTextLabel}
              cartSpendingGiftTitleLabel={cartSpendingGiftTitleLabel}
              dataGiftNoteStatus={dataGiftNoteStatus}
              linkAriaLabel={linkAriaLabel}
              subtotalLabel={subtotalLabelCartDrawer}
              {...styles.drawer}
            />

            {/* Gift Note Drawer */}
            <CartGiftNoteDrawer
              accessibilityProps={{
                "aria-modal": true,
                ref: setNestedDrawerRef,
                role: "dialog",
                tabIndex: -1,
              }}
              dataErrorMessage={dataErrorMessage}
              dataGiftNoteDrawer={dataGiftNoteDrawer}
              show={cartDrawerOpen && giftNoteDrawerOpen}
              {...styles.drawer}
            />

            {/* Gift With Purchase Drawer */}
            <CartGiftWithPurchaseDrawer
              accessibilityProps={{
                "aria-modal": true,
                ref: setNestedDrawerRef,
                role: "dialog",
                tabIndex: -1,
              }}
              show={cartDrawerOpen && giftWithPurchaseDrawerOpen}
              valueLabel={valueLabel}
              {...styles.drawer}
            />

            {/* Product Variant Drawer */}
            <CartProductVariantDrawer
              accessibilityProps={{
                "aria-modal": true,
                ref: setNestedDrawerRef,
                role: "dialog",
                tabIndex: -1,
              }}
              show={
                !!(activeProduct && cartDrawerOpen && productVariantDrawerOpen)
              }
              {...styles.drawer}
            />
          </div>
        </div>
      </Transition>

      <CartDrawerAnalytics />
    </>
  );
};

export const CartDrawerManager = memo(CartDrawerManagerBase);
